export type NucleusExplainActivationResult =
  | Readonly<{ status: "accepted" }>
  | Readonly<{ status: "rejected"; reason: string }>;

export type NucleusExplainActivationOutcome =
  NucleusExplainActivationResult | void;

export type NucleusExplainActivation =
  | NucleusExplainActivationOutcome
  | Promise<NucleusExplainActivationOutcome>;

export type NucleusExplainActivationHandler = () => NucleusExplainActivation;

export const NUCLEUS_EXPLAIN_ACCEPTED = Object.freeze({
  status: "accepted",
} as const);

export const NUCLEUS_EXPLAIN_STALE_REASON =
  "This value is no longer ready to explain. Refresh the view and try again.";

export const NUCLEUS_EXPLAIN_BUSY_REASON =
  "Nucleus is already responding. Stop or wait before explaining another value.";

export const NUCLEUS_EXPLAIN_STARTING_REASON = "Starting this explanation…";

export const NUCLEUS_EXPLAIN_START_FAILED_REASON =
  "Nucleus could not start that explanation. Please try again.";

/** Columns whose date-pivot body values can carry an exact Explain action. */
export const isNucleusExplainDatePivotValueColumn = (
  columnKey: string,
): boolean => {
  if (columnKey === "TOTAL") return true;
  if (!/^[1-9][0-9]{0,15}$/.test(columnKey)) return false;
  const epoch = Number(columnKey);
  if (!Number.isSafeInteger(epoch) || String(epoch) !== columnKey) return false;
  const date = new Date(epoch);
  return (
    !Number.isNaN(date.getTime()) &&
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
};

export const NUCLEUS_EXPLAIN_DIRTY_PO_REASON =
  "Save or discard the row edits before asking Nucleus about this proposal.";

export const NUCLEUS_EXPLAIN_PENDING_PO_REASON =
  "Wait for the pending approval change to finish before asking Nucleus about this proposal.";

export const rejectNucleusExplainActivation = (
  reason: string,
): NucleusExplainActivationResult =>
  Object.freeze({
    status: "rejected",
    reason,
  });

export const awaitNucleusExplainStart = async (
  connect: () => Promise<boolean> | void,
  clearPending: () => void,
  markReady: () => boolean,
): Promise<NucleusExplainActivationResult> => {
  try {
    if ((await connect()) === true) {
      if (markReady()) return NUCLEUS_EXPLAIN_ACCEPTED;
      clearPending();
      return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
    }
  } catch {
    // The customer-facing rejection below intentionally owns transport errors.
  }
  clearPending();
  return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_START_FAILED_REASON);
};

export const isRejectedNucleusExplainActivation = (
  result: NucleusExplainActivationOutcome,
): result is Extract<NucleusExplainActivationResult, { status: "rejected" }> =>
  result?.status === "rejected";
